import { useEffect, useRef } from 'react'
import { analytics, useScrollTracking, useTimeTracking } from '../utils/analytics'

// Hook for tracking component interactions
export const useComponentTracking = (componentName: string) => {
  const mountTime = useRef<number>()

  useEffect(() => {
    mountTime.current = Date.now()
    
    return () => {
      if (mountTime.current) {
        const duration = Date.now() - mountTime.current
        analytics.trackEngagement('component_view', componentName, duration)
      }
    }
  }, [componentName])

  const trackInteraction = (action: string, element?: string) => {
    analytics.trackEngagement(action, element || componentName)
  }

  return { trackInteraction }
}

// Hook for tracking form interactions
export const useFormTracking = (formName: string) => {
  const startTime = useRef<number>()
  const fieldInteractions = useRef<Record<string, number>>({})

  const trackFormStart = () => {
    startTime.current = Date.now()
    analytics.trackContactForm('start', formName)
  }

  const trackFieldFocus = (fieldName: string) => {
    fieldInteractions.current[fieldName] = Date.now()
    analytics.trackEngagement('field_focus', `${formName}_${fieldName}`)
  }

  const trackFieldBlur = (fieldName: string) => {
    const focusTime = fieldInteractions.current[fieldName]
    if (focusTime) {
      const duration = Date.now() - focusTime
      analytics.trackEngagement('field_interaction', `${formName}_${fieldName}`, duration)
    }
  }

  const trackFormSubmit = () => {
    const duration = startTime.current ? Date.now() - startTime.current : 0
    analytics.trackContactForm('submit', `${formName}_duration_${Math.round(duration / 1000)}s`)
  }

  const trackFormSuccess = () => {
    analytics.trackContactForm('success', formName)
  }

  const trackFormError = (error: string) => {
    analytics.trackContactForm('error', `${formName}_${error}`)
  }

  return {
    trackFormStart,
    trackFieldFocus,
    trackFieldBlur,
    trackFormSubmit,
    trackFormSuccess,
    trackFormError,
  }
}

// Hook for tracking navigation
export const useNavigationTracking = () => {
  const trackNavClick = (section: string, method: 'click' | 'scroll' = 'click') => {
    analytics.trackNavigation(`${section}_${method}`)
  }

  const trackExternalLink = (url: string, context: string) => {
    analytics.trackEvent('click', 'External Link', `${context}_${url}`)
  }

  return { trackNavClick, trackExternalLink }
}

// Hook for tracking downloads
export const useDownloadTracking = () => {
  const trackDownload = (fileName: string, fileType: string, context?: string) => {
    analytics.trackDownload(fileName, fileType)
    
    if (context) {
      analytics.trackEvent('download', 'Context', `${context}_${fileName}`)
    }
  }

  return { trackDownload }
}

// Hook for tracking social media interactions
export const useSocialTracking = () => {
  const trackSocialClick = (platform: string, action: string = 'click') => {
    analytics.trackSocialClick(platform, action)
  }

  const trackSocialShare = (platform: string, content: string) => {
    analytics.trackEvent('share', 'Social Media', `${platform}_${content}`)
  }

  return { trackSocialClick, trackSocialShare }
}

// Hook for tracking project interactions
export const useProjectTracking = () => {
  const trackProjectView = (projectName: string) => {
    analytics.trackProjectView(projectName)
  }

  const trackProjectLink = (projectName: string, linkType: 'demo' | 'github' | 'details') => {
    analytics.trackProjectLink(projectName, linkType as 'demo' | 'github')
  }

  const trackProjectFilter = (filterType: string, filterValue: string) => {
    analytics.trackEvent('filter', 'Projects', `${filterType}_${filterValue}`)
  }

  return { trackProjectView, trackProjectLink, trackProjectFilter }
}

// Hook for tracking theme changes
export const useThemeTracking = () => {
  const trackThemeChange = (theme: 'light' | 'dark') => {
    analytics.trackThemeChange(theme)
  }

  return { trackThemeChange }
}

// Hook for comprehensive page tracking
export const usePageTracking = (pageName: string) => {
  const scrollHandler = useScrollTracking()
  const timeTracker = useTimeTracking()

  useEffect(() => {
    // Track page view
    analytics.trackEvent('page_view', 'Navigation', pageName)

    // Set up scroll tracking
    window.addEventListener('scroll', scrollHandler, { passive: true })

    // Cleanup
    return () => {
      window.removeEventListener('scroll', scrollHandler)
      timeTracker() // Final time report
    }
  }, [pageName, scrollHandler, timeTracker])

  return {
    trackPageInteraction: (action: string, element: string) => {
      analytics.trackEngagement(action, `${pageName}_${element}`)
    }
  }
}

// Hook for tracking performance metrics
export const usePerformanceTracking = () => {
  useEffect(() => {
    // Track page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
        
        if (navigation) {
          const loadTime = navigation.loadEventEnd - navigation.fetchStart
          const domContentLoaded = navigation.domContentLoadedEventEnd - navigation.fetchStart
          const firstPaint = performance.getEntriesByName('first-paint')[0]?.startTime || 0
          
          analytics.trackCustomEvent('performance_metrics', {
            load_time: Math.round(loadTime),
            dom_content_loaded: Math.round(domContentLoaded),
            first_paint: Math.round(firstPaint),
          })
        }
      }, 1000)
    })
  }, [])
}

// Hook for tracking user engagement patterns
export const useEngagementTracking = () => {
  const sessionStart = useRef(Date.now())
  const lastActivity = useRef(Date.now())
  const activityCount = useRef(0)

  useEffect(() => {
    const trackActivity = () => {
      lastActivity.current = Date.now()
      activityCount.current++
    }

    const events = ['click', 'scroll', 'keypress', 'mousemove']
    events.forEach(event => {
      document.addEventListener(event, trackActivity, { passive: true })
    })

    // Track session end on page unload
    const handleUnload = () => {
      const sessionDuration = Date.now() - sessionStart.current
      const idleTime = Date.now() - lastActivity.current
      
      analytics.trackCustomEvent('session_end', {
        session_duration: Math.round(sessionDuration / 1000),
        activity_count: activityCount.current,
        idle_time: Math.round(idleTime / 1000),
      })
    }

    window.addEventListener('beforeunload', handleUnload)

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, trackActivity)
      })
      window.removeEventListener('beforeunload', handleUnload)
    }
  }, [])
}